import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { useToast } from "../../components/Feedback";
import { Panel } from "../../components/Layout";
import { QueryState } from "../../components/States";
import { dateTime } from "../../lib/format";

type Group = Record<string, number>;
type Profile = Record<string, unknown> & { profile_name: string };

const GROUPS: { key: string; title: string; help: string; unit?: string }[] = [
  { key: "weights", title: "Dimension weights", help: "How the multidimensional score combines its parts. Saved as shares of 100." },
  { key: "condition_bands", title: "Condition bands", help: "Lowest score for Good and Fair; anything below Fair is Poor." },
  { key: "rdd_penalty_per_detection", title: "Points lost per verified defect", help: "Taken off 100 for each verified defect of that type in a section." },
  { key: "rdd_penalty_caps", title: "Maximum points lost per defect type", help: "Caps the loss from one defect type in a section." },
  { key: "priority_weights", title: "Priority weights", help: "How condition need, safety, drainage, road class and accelerating distress combine into priority." },
  { key: "priority_bands", title: "Priority bands", help: "Lowest priority score for Very High, High and Medium." },
  { key: "cost_rates_kes_per_m2", title: "Treatment cost rates", help: "Indicative rates used for section costs.", unit: "KES/m²" },
  { key: "default_carriageway_width_m", title: "Default carriageway width", help: "Used for areas when no RICS width is known.", unit: "m" },
];

const LABELS: Record<string, string> = {
  condition: "Condition", facilities: "Facilities", usage: "Usage", functional_class: "Functional class", material: "Material", design: "Design",
  good_min: "Good from", fair_min: "Fair from", poor_min: "Poor from", condition_need: "Condition need", safety: "Safety", drainage: "Drainage",
  strategic: "Strategic (road class)", accelerating_distress: "Accelerating distress", very_high_min: "Very High from", high_min: "High from",
  medium_min: "Medium from", D00: "D00 Longitudinal crack", D10: "D10 Transverse crack", D20: "D20 Alligator crack", D40: "D40 Pothole", OTHER: "Other defects",
};

function GroupEditor({ g, value, onChange }: { g: (typeof GROUPS)[number]; value: Group; onChange: (v: Group) => void }) {
  const total = Object.values(value).reduce((a, b) => a + b, 0);
  return (
    <fieldset className="panel" style={{ padding: 16, margin: 0 }}>
      <legend className="field-label" style={{ padding: "0 6px" }}>{g.title}</legend>
      <p className="field-hint" style={{ marginBottom: 12 }}>{g.help}{g.key.endsWith("weights") ? ` Current total ${total.toFixed(1)}.` : ""}</p>
      <div className="form-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))" }}>
        {Object.entries(value).map(([k, v]) => (
          <div className="field" key={k}>
            <label className="field-label" htmlFor={`de-${g.key}-${k}`} style={{ fontWeight: 500 }}>{LABELS[k] ?? k}{g.unit ? ` (${g.unit})` : ""}</label>
            <input id={`de-${g.key}-${k}`} className="input num" type="number" min="0" step="any" value={v}
                   onChange={(e) => onChange({ ...value, [k]: e.target.value === "" ? 0 : Number(e.target.value) })} />
          </div>
        ))}
      </div>
    </fieldset>
  );
}

export function DecisionTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ["admin", "de"], queryFn: () => api<{ profiles: { profile_name: string; profile_json: string; is_active: number; updated_at: string }[]; assessed_surveys: number }>("/api/admin/decision-engine") });
  const active = q.data?.profiles.find((p) => Number(p.is_active)) ?? q.data?.profiles[0];
  const [draft, setDraft] = useState<Profile | null>(null);
  useEffect(() => { if (active) setDraft(JSON.parse(active.profile_json)); }, [active?.profile_json]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = useMutation({
    mutationFn: () => api<{ assessed: unknown[]; skipped: { reason: string }[] }>("/api/admin/decision-engine/run", { method: "POST", json: {} }),
    onSuccess: (r) => { qc.invalidateQueries(); toast({ text: `Recalculated ${r.assessed.length} surveys${r.skipped.length ? `; ${r.skipped.length} skipped (no GPS chainage)` : ""}.` }); },
    onError: (e) => toast({ text: e instanceof Error ? e.message : "Recalculation failed.", tone: "error" }),
  });
  const save = useMutation({
    mutationFn: () => api("/api/admin/decision-engine/profile", { method: "PUT", json: { profile: draft } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "de"] }); toast({ text: "Profile saved. Recalculate to apply it to existing surveys.", action: { label: "Recalculate", run: () => run.mutate() } }, 10000); },
    onError: (e) => toast({ text: e instanceof Error ? e.message : "The profile could not be saved.", tone: "error" }),
  });

  return (
    <QueryState query={q}>
      {(d) => (
        <>
          <Panel title="Ratings" id="de-run" actions={<button className="btn btn-primary btn-sm" disabled={run.isPending} onClick={() => run.mutate()}>{run.isPending ? "Recalculating…" : "Recalculate all surveys"}</button>}>
            <p>{d.assessed_surveys} surveys have section ratings. Ratings also update by themselves after every review decision, manual defect or image removal.</p>
            <p className="muted" style={{ marginTop: 8, fontSize: 13 }}>Condition counts verified defects only. Sections with AI findings still waiting for review are shown as provisional.</p>
          </Panel>
          {draft ? (
            <form className="stack" style={{ gap: 16 }} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
              <Panel title={<>Active profile: {draft.profile_name}</>} id="de-profile">
                <p className="muted" style={{ fontSize: 13 }}>Last saved {dateTime(active?.updated_at)}. Saving asks for your password again because it changes every road's rating.</p>
              </Panel>
              {GROUPS.filter((g) => draft[g.key]).map((g) => (
                <GroupEditor key={g.key} g={g} value={draft[g.key] as Group} onChange={(v) => setDraft({ ...draft, [g.key]: v })} />
              ))}
              <div className="form-actions">
                <button type="button" className="btn" onClick={() => active && setDraft(JSON.parse(active.profile_json))}>Discard changes</button>
                <button type="submit" className="btn btn-primary" disabled={save.isPending}>Save profile</button>
              </div>
            </form>
          ) : null}
        </>
      )}
    </QueryState>
  );
}
